import { useCallback, useMemo, useState } from "react";
import { BondFilterSectionGap, BondFilterSectionMargin } from "../BondFilterConst";
import Decimal from "decimal.js";
import { BondFilterCriteria } from "../BondFilterCriteria";
import { BondFilterDataConstraints } from "../BondFilterDataConstraints";
import { BondFilter, BondFilterChipData, BondFilterData, BondFilterProps } from "../BondFilter";
import { PricedBond } from "../../../PricedBond";
import { asAmount, asBondPriceNominal, BondPriceNominal } from "@goodfill/domain-properties";
import { formatPercentage } from "@goodfill/format-number";
import { InputNumberFormatter, AccordionSection, Flex, InputRange, InputNumber } from "@goodfill/ui-components";
import {
  getSessionStorageDecimal,
  removeItemFromSessionStorage,
  saveDecimalToSessionStorage,
} from "../../../../../sessionStorage";

const FILTER_LOW_STORAGE_KEY = "filter:purchasePriceLowerBound";
const FILTER_HIGHT_STORAGE_KEY = "filter:purchasePriceHigherBound";

function percentsToBondPriceNominal(value: number) {
  return asBondPriceNominal(new Decimal(value).div(100));
}

const _Component = (props: BondFilterProps) => {
  const purchasePriceLowerBound = props.criteria.purchasePriceLowerBound;
  const purchasePriceHigherBound = props.criteria.purchasePriceHigherBound;
  const purchasePriceStep = asAmount(new Decimal(0.0001));
  const purchasePriceMin = props.constraints.purchasePriceMin;
  const purchasePriceMax = props.constraints.purchasePriceMax;
  const [purchasePriceSectionIsOpen, setPurchasePriceSectionIsOpen] = useState(false);

  const purchasePriceLabel = "Purchase Price";

  const { minLeft, maxLeft, minRight, maxRight, leftBound, rightBound, step } = useMemo<{
    minLeft: number;
    maxLeft: number;
    minRight: number;
    maxRight: number;
    leftBound: number;
    rightBound: number;
    step: number;
  }>(() => {
    const minLeft = purchasePriceMin.mul(100).toDecimalPlaces(2).toNumber();
    const minRight = purchasePriceLowerBound.plus(purchasePriceStep).toNumber();
    const maxRight = purchasePriceMax.mul(100).toDecimalPlaces(2).toNumber();
    const leftBound = purchasePriceLowerBound.mul(100).toDecimalPlaces(2).toNumber();
    const rightBound = purchasePriceHigherBound.mul(100).toDecimalPlaces(2).toNumber();
    const step = purchasePriceStep.mul(100).toDecimalPlaces(2).toNumber();
    const maxLeft = rightBound - step;

    return {
      minLeft,
      maxLeft,
      minRight,
      maxRight,
      leftBound,
      rightBound,
      step,
    };
  }, [purchasePriceMin, purchasePriceHigherBound, purchasePriceLowerBound, purchasePriceMax, purchasePriceStep]);

  const formatPurchasePrice = useCallback<InputNumberFormatter>((value) => {
    return formatPercentage({
      numDecimals: 2,
      value,
      multiplier: 0.01, // value is already x100 to make it work in the input
    });
  }, []);

  const onChangeCriteria = useCallback(
    (min: BondPriceNominal, max: BondPriceNominal) => {
      props.onChange({
        criteria: {
          ...props.criteria,
          purchasePriceLowerBound: min,
          purchasePriceHigherBound: max,
        },
      });
    },
    [props],
  );

  const onRightChange = useCallback(
    (right: number) => onChangeCriteria(purchasePriceLowerBound, percentsToBondPriceNominal(right)),
    [purchasePriceLowerBound, onChangeCriteria],
  );

  const onLeftChange = useCallback(
    (left: number) => onChangeCriteria(percentsToBondPriceNominal(left), purchasePriceHigherBound),
    [purchasePriceHigherBound, onChangeCriteria],
  );

  return (
    <AccordionSection
      title={purchasePriceLabel}
      isOpen={purchasePriceSectionIsOpen}
      setIsOpen={setPurchasePriceSectionIsOpen}
    >
      <Flex direction="col" verticalMargin={BondFilterSectionMargin} gap={BondFilterSectionGap}>
        <InputRange
          min={minLeft}
          max={maxRight}
          step={step}
          leftBound={leftBound}
          rightBound={rightBound}
          setLeftBound={onLeftChange}
          setRightBound={onRightChange}
        />

        <Flex direction="row" justify="between" gap={4}>
          <InputNumber
            border={"neutral"}
            width={"w-20"}
            transparent={true}
            textAlign="center"
            min={minLeft}
            max={maxLeft}
            step={step}
            value={leftBound}
            setValue={(value) => {
              if (value === null) return;
              onLeftChange(value);
            }}
            formatValue={formatPurchasePrice}
          />

          <InputNumber
            border={"neutral"}
            width={"w-20"}
            transparent={true}
            textAlign="center"
            min={minRight}
            max={maxRight}
            step={step}
            value={rightBound}
            setValue={(value) => {
              if (value === null) return;
              onRightChange(value);
            }}
            formatValue={formatPurchasePrice}
          />
        </Flex>
      </Flex>
    </AccordionSection>
  );
};

export class BondFilterPurchasePrice implements BondFilter {
  isVisibleInDropdown(): boolean {
    return true;
  }

  reset(criteria: BondFilterCriteria, defaultCriteria: BondFilterCriteria): BondFilterCriteria {
    return {
      ...criteria,
      purchasePriceLowerBound: defaultCriteria.purchasePriceLowerBound,
      purchasePriceHigherBound: defaultCriteria.purchasePriceHigherBound,
    };
  }

  getChips(criteria: BondFilterCriteria, defaultCriteria: BondFilterCriteria): BondFilterChipData[] {
    const isActive =
      !criteria.purchasePriceLowerBound.equals(defaultCriteria.purchasePriceLowerBound) ||
      !criteria.purchasePriceHigherBound.equals(defaultCriteria.purchasePriceHigherBound);

    return isActive
      ? [
          {
            id: "price",
            label: "Price",
            onClick: () => this.reset(criteria, defaultCriteria),
          },
        ]
      : [];
  }

  getDefaultCriteriaConstraints(pricedBond: PricedBond): Partial<BondFilterDataConstraints> {
    const roundedBidPrice = asBondPriceNominal(pricedBond.bidPrice.toDecimalPlaces(4));

    return {
      purchasePriceMin: roundedBidPrice,
      purchasePriceMax: roundedBidPrice,
    };
  }

  afterCriteriaCalculation(current: BondFilterDataConstraints): BondFilterDataConstraints {
    return {
      ...current,
      purchasePriceMin: asBondPriceNominal(current.purchasePriceMin.minus(0.0001)),
      purchasePriceMax: asBondPriceNominal(current.purchasePriceMax.plus(0.0001)),
    };
  }

  evaluate(data: BondFilterData, criteria: BondFilterCriteria): boolean {
    const roundedBidPrice = data.pricedBond.bidPrice.toDecimalPlaces(4);

    return (
      roundedBidPrice.greaterThanOrEqualTo(criteria.purchasePriceLowerBound) &&
      roundedBidPrice.lessThanOrEqualTo(criteria.purchasePriceHigherBound)
    );
  }

  updateConstraintByPricedBond(pricedBond: PricedBond, current: BondFilterDataConstraints): BondFilterDataConstraints {
    const roundedBidPrice = asBondPriceNominal(pricedBond.bidPrice.toDecimalPlaces(4));

    const result = { ...current };

    if (roundedBidPrice.greaterThan(result.purchasePriceMax)) {
      result.purchasePriceMax = roundedBidPrice;
    }
    if (roundedBidPrice.lessThan(result.purchasePriceMin)) {
      result.purchasePriceMin = roundedBidPrice;
    }

    return result;
  }

  component = _Component;

  applyCriteriaFromStorage(criteria: Partial<BondFilterCriteria>): Partial<BondFilterCriteria> {
    const rawLow = getSessionStorageDecimal(FILTER_LOW_STORAGE_KEY);
    const rawHight = getSessionStorageDecimal(FILTER_HIGHT_STORAGE_KEY);

    return {
      ...criteria,
      purchasePriceLowerBound: rawLow ? asBondPriceNominal(rawLow) : undefined,
      purchasePriceHigherBound: rawHight ? asBondPriceNominal(rawHight) : undefined,
    };
  }

  storeCriteriaToStorage(criteria: BondFilterCriteria, defaultCriteria: BondFilterCriteria): void {
    if (criteria.purchasePriceLowerBound.equals(defaultCriteria.purchasePriceLowerBound)) {
      removeItemFromSessionStorage(FILTER_LOW_STORAGE_KEY);
    } else {
      saveDecimalToSessionStorage(FILTER_LOW_STORAGE_KEY, criteria.purchasePriceLowerBound);
    }

    if (criteria.purchasePriceHigherBound.equals(defaultCriteria.purchasePriceHigherBound)) {
      removeItemFromSessionStorage(FILTER_HIGHT_STORAGE_KEY);
    } else {
      saveDecimalToSessionStorage(FILTER_HIGHT_STORAGE_KEY, criteria.purchasePriceHigherBound);
    }
  }
}
